import { ReactElement, useContext, useMemo } from "react";
import { CurrentExerciseContext } from "src/providers/CurrentExerciseProvider";
import { Banner } from "src/components/presentational/Banner";
import { TextContext } from "src/providers/TextProvider";
import { ExerciseType } from "src/sources/enums/exercise-type.enum";
import { CursorContext } from "src/providers/CursorProvider";
import { findAndExecute } from "src/commons/purefunctions/findAndExecute";
import { VIMTUTOR_EXERCISES } from "src/sources/generators/vimtutor-exercises.generator";

/**
 * This Component already only operates on the current exercise, and its main responsibilities are:
 * 1. Reseting the text to the original content
 * 2. Verify if the text is in the desired state.
 * 3. Update the context so the TextEditor can display the exercise.
 */
export function CurrentExercise(): ReactElement {
  const { current, setCurrent } = useContext(CurrentExerciseContext);
  const { text, setText } = useContext(TextContext);
  const { cursor } = useContext(CursorContext);

  const isExerciseComplete = useMemo(() => {
    const isTextInDesiredState = () => {
      return current.desiredState === text;
    };

    if (current.type === ExerciseType.EDITING) {
      return isTextInDesiredState();
    }
    return isTextInDesiredState() && cursor === current.desiredCursorPosition;
  }, [current, cursor, text]);

  const resetToInitialState = () => {
    setText(current.initialState);
  };

  return (
    <div>
      <h1>{current.title}</h1>
      <p style={{ whiteSpace: "pre-line" }}>{current.instructions}</p>
      {current.type !== ExerciseType.NAVIGATION && (
        <p style={{ whiteSpace: "pre-line" }}>
          Desired state: <br />
          {current.desiredState}
        </p>
      )}
      <p>
        {isExerciseComplete && (
          <Banner
            exerciseTitle={current.title}
            onNextClick={() =>
              findAndExecute(VIMTUTOR_EXERCISES, current.id + 1, (exercise) => {
                setCurrent(exercise);
                setText(exercise.initialState);
              })
            }
          />
        )}
      </p>
      <button onClick={resetToInitialState}>Reset</button>
    </div>
  );
}
